<template>
    <div class="tabs-component" :id="id">
        <ul role="tablist" class="tabs-component-tabs">
            <li v-for="(tab, i) in tabs" :key="i" class="tabs-component-tab" :class="{ 'is-active': tab.isActive, 'is-disabled': tab.isDisabled }" role="presentation">
                <a role="tab" class="tabs-component-tab-a" :aria-controls="tab.paneId" :aria-selected="tab.isActive" :href="tab.hash" @click="selectTab(tab.hash, $event)" tabindex="0" v-html="tab.header"></a>
            </li>
        </ul>
        <div>
            <slot></slot>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, provide } from "vue";
import expiringStorage from "./expiringStorage";

const props = defineProps({
    id: String,
    cacheLifetime: {
        type: Number,
        default: 5,
    },
    options: {
        type: Object,
        default: () => ({
            useUrlFragment: true,
            defaultTabHash: undefined,
            storageKey: undefined,
        }),
    },
});

const emit = defineEmits(["changed", "clicked"])
// const emit = defineEmits();

const tabs = ref([]);
const activeTabHash = ref("");
const lastActiveTabHash = ref("");

provide('tabsProvider', { activeTabHash, lastActiveTabHash, tabs });

provide('addTab', (tab) => tabs.value.push(tab));

provide('updateTab', (computedId, data) => {
    const tabIndex = tabs.value.findIndex((tab) => tab.computedId === computedId);
    data.isActive = tabs.value[tabIndex].isActive;
    tabs.value[tabIndex] = data;
});

provide('deleteTab', (computedId) => {
    const tabIndex = tabs.value.findIndex((tab) => tab.computedId === computedId);
    tabs.value.splice(tabIndex, 1);
});

const storageKey = computed(() => {
    let key = undefined;

    if (props.options.storageKey) {
        key = props.options.storageKey;
    }

    if (!key && props.id) {
        key = `vue-tabs-component.${props.id}.cache.${window.location.host}${window.location.pathname}`;
    }

    if (!key) {
        key = `vue-tabs-component.cache.${window.location.host}${window.location.pathname}`;
    }

    return key;
});

const selectTab = (selectedTabHash, event) => {
    if (event && !props.options.useUrlFragment) {
        event.preventDefault();
    }

    const selectedTab = findTab(selectedTabHash);

    if (!selectedTab) {
        return;
    }

    if (event && selectedTab.isDisabled) {
        event.preventDefault();
        return;
    }

    if (lastActiveTabHash.value === selectedTab.hash) {
        emit("clicked", { tab: selectedTab });
        return;
    }

    tabs.value.forEach(tab => {
        tab.isActive = (tab.hash === selectedTab.hash);
    })

    emit("changed", { tab: selectedTab });

    lastActiveTabHash.value = activeTabHash.value = selectedTab.hash;

    if (props.cacheLifetime <= 0) {
        return;
    }

    expiringStorage.set(storageKey.value, selectedTab.hash, props.cacheLifetime);
};

const findTab = (hash) => {
    return tabs.value.find(tab => tab.hash === hash);
};

onMounted(() => {
    if (!tabs.value.length) {
        return;
    }

    window.addEventListener("hashchange", () => selectTab(window.location.hash));

    if (findTab(window.location.hash)) {
        selectTab(window.location.hash);
        return;
    }

    if (props.options.defaultTabHash && findTab("#" + props.options.defaultTabHash)) {
        selectTab("#" + props.options.defaultTabHash);
        return;
    } else if (props.cacheLifetime > 0) {
        const previousSelectedTabHash = expiringStorage.get(storageKey.value);

        if (previousSelectedTabHash !== null && findTab(previousSelectedTabHash)) {
            selectTab(previousSelectedTabHash);
            return;
        }

        if (props.options.defaultTabHash && findTab("#" + props.options.defaultTabHash)) {
            selectTab("#" + props.options.defaultTabHash);
            return;
        }
    }

    selectTab(tabs.value[0].hash);
});

defineExpose({
    selectTab,
    findTab
})
</script>
